/**
 * Factory for the viewer-API client.
 *
 * Mirrors the backend's `regulatory_viewer_routes.build_viewer_router(family)`
 * pattern: one source of truth for the seven viewer endpoints
 * (TOC, pages, page text-spans single + batch, chunks single + batch,
 * full-text search, section-ref resolve), parameterised by the URL
 * prefix that maps to a regulatory family.
 *
 * Usage:
 *   const sbaApi = createViewerApi('/sba');
 *   const eparApi = createViewerApi('/epar');
 *   const ukPilApi = createViewerApi('/uk_pil');
 *
 * Each `*Api` exposes the same shape (`fetchTOC`, `fetchPages`,
 * `fetchTextSpans`, `fetchBatchTextSpans`, `fetchChunk`,
 * `fetchChunksBatch`, `resolveSectionRef`, `searchDocument`,
 * `buildImageUrl`) — call sites are family-agnostic once they
 * receive an `api` object.
 *
 * The legacy `viewerApi.ts` and `eparViewerApi.ts` modules continue
 * to expose their named exports for backward compatibility — they
 * now re-export the SBA/EPAR factory instances, so existing imports
 * keep working without changes.
 */
import type {
  TOCResponse,
  PagesResponse,
  TextSpansResponse,
  BatchTextSpansResponse,
  RegionsResponse,
  ChunkMetadata,
  ChunkBatchResponse,
  SectionLayoutsEnsureResponse,
  SectionTextResponse,
  SearchResponse,
} from '../types/viewer';
import { auth } from '../config/firebase';
import { getImpersonationHeaders } from '../services/impersonationHeaders';


// ─────────────────────────────────────────────────────────────────────
// Section-resolve types (used by viewerApi.resolveSectionRef)
// ─────────────────────────────────────────────────────────────────────

export interface SectionRefMatch {
  document_id: number;
  section_id: number;
  section_number: string;
  section_number_local: string | null;
  title: string;
  page_start: number | null;
  doc_type: string | null;
  /** Lowest chunk id in the matched section, for viewer-native
   *  navigation via ``RegulatoryViewerEmbed.highlightChunkIds``.  Null
   *  when the section has no persisted chunks. */
  first_chunk_id: number | null;
}

export interface ResolveSectionRefResponse {
  drug_id: number;
  section_ref: string;
  normalized: string | null;
  matches: SectionRefMatch[];
}


// ─────────────────────────────────────────────────────────────────────
// Internal helpers (shared across all factory instances)
// ─────────────────────────────────────────────────────────────────────

const BASE_URL =
  import.meta.env['VITE_API_URL'] ||
  'https://us-central1-label-doctor-f255e.cloudfunctions.net/apiProxy';

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) return {};
  const token = await user.getIdToken(false);
  return { Authorization: `Bearer ${token}` };
}

async function fetchWithAuth<T>(path: string): Promise<T> {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${BASE_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', ...headers, ...getImpersonationHeaders() },
  });
  if (!resp.ok) {
    throw new Error(`Viewer API error ${resp.status}: ${await resp.text()}`);
  }
  return (await resp.json()) as T;
}

async function postWithAuth<T>(
  path: string,
  body: unknown,
): Promise<T> {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers, ...getImpersonationHeaders() },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    throw new Error(`Viewer API error ${resp.status}: ${await resp.text()}`);
  }
  return (await resp.json()) as T;
}

/**
 * Build a full image URL with auth token as query param.
 * Used by <img src=...> which can't send Authorization headers.
 * Path-only — caller supplies the family prefix in the path.
 */
export async function buildImageUrl(path: string): Promise<string> {
  const user = auth.currentUser;
  if (!user) return `${BASE_URL}${path}`;
  const token = await user.getIdToken(false);
  return `${BASE_URL}${path}?token=${token}`;
}


// ─────────────────────────────────────────────────────────────────────
// The factory
// ─────────────────────────────────────────────────────────────────────

export interface ViewerApi {
  /** Family URL prefix (e.g. '/sba', '/epar', '/uk_pil') — exposed
   *  so callers that need to build raw image URLs (e.g. <img src>)
   *  can construct them family-correctly. */
  prefix: string;

  fetchTOC: (docId: number) => Promise<TOCResponse>;
  fetchPages: (docId: number) => Promise<PagesResponse>;
  fetchTextSpans: (docId: number, pageNum: number) => Promise<TextSpansResponse>;
  fetchBatchTextSpans: (docId: number, from: number, to: number) => Promise<BatchTextSpansResponse>;
  /** Lazy-load line/table regions for a page range (split out of fetchPages
   *  so large JP docs don't ship all regions eagerly). Mirrors
   *  fetchBatchTextSpans. */
  fetchBatchRegions: (docId: number, from: number, to: number) => Promise<RegionsResponse>;
  fetchChunk: (docId: number, chunkId: number) => Promise<ChunkMetadata>;
  fetchChunksBatch: (docId: number, chunkIds: number[]) => Promise<ChunkBatchResponse>;
  /** Parsed-chunk text of the given sections (one CSV ``ids`` param). */
  fetchSectionText: (docId: number, ids: number[]) => Promise<SectionTextResponse>;
  /** Queue page-layout generation for the sections' documents (enqueue) or poll its
   *  progress (enqueue=false). Scope families only (eu/au/uk_pil/ca_pm/epar). */
  ensureSectionLayouts: (docId: number, ids: number[], enqueue: boolean) => Promise<SectionLayoutsEnsureResponse>;
  resolveSectionRef: (drugId: number, sectionRef: string) => Promise<ResolveSectionRefResponse>;
  /** Resolve an AI-evidence snippet to the best-matching chunk id for
   *  this document (trigram match server-side). Returns the chunk id —
   *  ready to pass to ``RegulatoryViewerEmbed.highlightChunkIds`` — or
   *  null when no chunk clears the similarity threshold. */
  resolveChunk: (
    docId: number,
    evidenceText: string,
    sectionNumber?: string | null,
  ) => Promise<number | null>;
  searchDocument: (docId: number, query: string, limit?: number) => Promise<SearchResponse>;
}

/**
 * Build a ViewerApi bound to a regulatory family's URL prefix.
 *
 * The returned object has methods for all seven viewer endpoints +
 * the section-resolve endpoint.  Each method's URL is built by
 * concatenating `prefix` with the path tail; nothing else is family-
 * specific in the API client (auth, retries, JSON parsing are
 * universal).
 *
 * `prefix` should NOT include a leading `/api/` — that's part of
 * `BASE_URL` already.  Pass `/sba`, `/epar`, `/uk_pil` etc.
 */
export function createViewerApi(prefix: string): ViewerApi {
  return {
    prefix,

    fetchTOC(docId) {
      return fetchWithAuth<TOCResponse>(`${prefix}/documents/${docId}/toc`);
    },

    fetchPages(docId) {
      return fetchWithAuth<PagesResponse>(`${prefix}/documents/${docId}/pages`);
    },

    fetchTextSpans(docId, pageNum) {
      return fetchWithAuth<TextSpansResponse>(
        `${prefix}/documents/${docId}/pages/${pageNum}/text-spans`,
      );
    },

    fetchBatchTextSpans(docId, from, to) {
      return fetchWithAuth<BatchTextSpansResponse>(
        `${prefix}/documents/${docId}/pages/text-spans?from=${from}&to=${to}`,
      );
    },

    fetchBatchRegions(docId, from, to) {
      return fetchWithAuth<RegionsResponse>(
        `${prefix}/documents/${docId}/pages/regions?from=${from}&to=${to}`,
      );
    },

    fetchChunk(docId, chunkId) {
      return fetchWithAuth<ChunkMetadata>(
        `${prefix}/documents/${docId}/chunks/${chunkId}`,
      );
    },

    fetchSectionText(docId, ids) {
      return fetchWithAuth<SectionTextResponse>(
        `${prefix}/documents/${docId}/section-text?ids=${ids.join(',')}`,
      );
    },

    ensureSectionLayouts(docId, ids, enqueue) {
      return postWithAuth<SectionLayoutsEnsureResponse>(
        `${prefix}/documents/${docId}/section-layouts/ensure`,
        { ids, enqueue },
      );
    },

    fetchChunksBatch(docId, chunkIds) {
      return postWithAuth<ChunkBatchResponse>(
        `${prefix}/documents/${docId}/chunks/batch`,
        { chunk_ids: chunkIds },
      );
    },

    resolveSectionRef(drugId, sectionRef) {
      const params = new URLSearchParams({
        drug_id: String(drugId),
        section_ref: sectionRef,
      });
      return fetchWithAuth<ResolveSectionRefResponse>(
        `${prefix}/sections/resolve?${params.toString()}`,
      );
    },

    async resolveChunk(docId, evidenceText, sectionNumber) {
      const resp = await postWithAuth<{ chunk_id: number | null }>(
        `${prefix}/documents/${docId}/resolve-chunk`,
        {
          evidence_text: evidenceText,
          section_number: sectionNumber ?? null,
        },
      );
      return resp.chunk_id;
    },

    // In-document search is scoped to ONE PDF, so the result set is bounded by
    // the document's matching page count — no default limit (a cap would hide
    // matching pages and under-report the count). Pass an explicit limit only
    // if a caller ever wants to truncate.
    searchDocument(docId, query, limit) {
      const q = encodeURIComponent(query);
      const lim = limit != null ? `&limit=${limit}` : '';
      return fetchWithAuth<SearchResponse>(
        `${prefix}/documents/${docId}/search?q=${q}${lim}`,
      );
    },
  };
}


// ─────────────────────────────────────────────────────────────────────
// Pre-built family instances
// ─────────────────────────────────────────────────────────────────────
// Pre-built so call sites can `import { sbaApi } from './createViewerApi'`
// and use `sbaApi.fetchTOC(...)` directly without re-building the
// factory.  The legacy `viewerApi.ts` / `eparViewerApi.ts` files
// re-export from these.

export const sbaApi = createViewerApi('/sba');
export const eparApi = createViewerApi('/epar');
export const ukPilApi = createViewerApi('/uk_pil');
export const caPmApi = createViewerApi('/ca_pm');
export const auApi = createViewerApi('/au');
export const jpPdfApi = createViewerApi('/jp_pdf');
export const zhPdfApi = createViewerApi('/zh_pdf');
export const chPdfApi = createViewerApi('/ch_pdf');
export const dePdfApi = createViewerApi('/de_pdf');
export const iePdfApi = createViewerApi('/ie_pdf');
export const itPdfApi = createViewerApi('/it_pdf');
export const fdaLabelPdfApi = createViewerApi('/fda_label_pdf');
export const euApi = createViewerApi('/eu');
export const approvalLetterApi = createViewerApi('/approval-letters');
// Archived EU historical SmPC single-version viewer — docId is an
// eu_historical_documents.id; pages/text-spans/toc are served on demand from
// the SOR PDF by the /api/eu-history adapter (no ingested tables).
export const euHistoryApi = createViewerApi('/eu-history');
